
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { X, Save } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ImageUpload } from './ImageUpload';

interface PortfolioItemData {
  id: string;
  title: string;
  description?: string;
  image_url?: string;
  project_url?: string;
}

interface EditPortfolioItemProps {
  item: PortfolioItemData;
  onUpdate: () => void;
  onCancel: () => void;
}

export const EditPortfolioItem: React.FC<EditPortfolioItemProps> = ({
  item,
  onUpdate,
  onCancel
}) => {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: item.title,
    description: item.description || '',
    imageUrl: item.image_url || '',
    projectUrl: item.project_url || ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('portfolio_items')
        .update({
          title: formData.title,
          description: formData.description || null,
          image_url: formData.imageUrl || null,
          project_url: formData.projectUrl || null
        })
        .eq('id', item.id)
        .eq('user_id', user.id);

      if (error) throw error;

      toast.success('Portfolio item updated successfully');
      onUpdate();
    } catch (error) {
      console.error('Error updating portfolio item:', error);
      toast.error('Failed to update portfolio item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageChange = (imageUrl: string | null) => {
    setFormData(prev => ({ ...prev, imageUrl: imageUrl || '' }));
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Edit Portfolio Item</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              required
              placeholder="Project title"
            />
          </div>
          
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of your project"
              rows={3}
            />
          </div>
          
          <ImageUpload
            currentImageUrl={formData.imageUrl || undefined}
            onImageChange={handleImageChange}
          />
          
          <div>
            <Label htmlFor="projectUrl">Project URL</Label>
            <Input
              id="projectUrl"
              type="url"
              value={formData.projectUrl}
              onChange={(e) => setFormData(prev => ({ ...prev, projectUrl: e.target.value }))}
              placeholder="https://example.com/project"
            />
          </div>
          
          <div className="flex gap-2">
            <Button type="submit" disabled={isSubmitting || !formData.title}>
              <Save className="h-4 w-4 mr-2" />
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
